import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from '../../../config/dayjs';
import type { SupportTicketDetail, TicketCategory, TicketPriority } from '../../../types/support';
import { BadgeStyle } from '../../../shared/enums/status';
import { StatusBadgeCell } from '../components/cells';
import {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  TICKET_STATUS_BADGE_STYLE,
  ticketCategoryLabelKey,
  ticketOriginLabelKey,
  ticketPriorityLabelKey,
  ticketStatusLabelKey,
} from './ticketMeta';

interface TicketDetailHeaderProps {
  ticket: SupportTicketDetail;
  /** SUPPORT_VIEW-only agents see these as read-only pills, not selects. */
  canManage: boolean;
  onCategoryChange: (category: TicketCategory) => void;
  onPriorityChange: (priority: TicketPriority) => void;
  isUpdating: boolean;
  onBack: () => void;
  /** Assign / resolve buttons, right-aligned (wrapped below on phones). */
  actions?: ReactNode;
}

const badge = (style: BadgeStyle, label: string) => (
  <StatusBadgeCell
    badgeClass={`status-badge-${style}`}
    dotClass={`status-dot-${style}`}
    label={label}
  />
);

/** Back, subject, status/origin/category/priority/language, escalation reason. */
export function TicketDetailHeader({
  ticket,
  canManage,
  onCategoryChange,
  onPriorityChange,
  isUpdating,
  onBack,
  actions,
}: TicketDetailHeaderProps) {
  const { t } = useTranslation();
  const { prompt, completion } = ticket.tokenTotals;

  return (
    <header className="chat-header">
      <button
        type="button"
        className="chat-back"
        onClick={onBack}
        aria-label={t('dashboard.support.backToQueue')}
      >
        <span className="material-symbols-outlined" aria-hidden="true">arrow_back</span>
      </button>

      <div className="chat-header-main">
        <h2 className={`chat-title ${ticket.subject ? '' : 'is-empty'}`} title={ticket.subject}>
          {ticket.subject || t('dashboard.support.untitled')}
        </h2>

        <div className="chat-meta">
          {badge(TICKET_STATUS_BADGE_STYLE[ticket.status], t(ticketStatusLabelKey(ticket.status)))}
          {ticket.origin === 'agent' &&
            badge(BadgeStyle.IN_USE, t(ticketOriginLabelKey('agent')))}

          {/* Native selects: the shared MuiTextField override paints outlined
              fields dark for the mobile app, which is unreadable here. */}
          {canManage ? (
            <select
              className="chat-meta-select"
              aria-label={t('dashboard.support.categoryLabel')}
              value={ticket.category}
              disabled={isUpdating}
              onChange={(e) => onCategoryChange(e.target.value as TicketCategory)}
            >
              {TICKET_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {t(ticketCategoryLabelKey(category))}
                </option>
              ))}
            </select>
          ) : (
            <span className="chat-meta-pill">{t(ticketCategoryLabelKey(ticket.category))}</span>
          )}

          {canManage ? (
            <select
              className="chat-meta-select"
              aria-label={t('dashboard.support.priorityLabel')}
              value={ticket.priority}
              disabled={isUpdating}
              onChange={(e) => onPriorityChange(e.target.value as TicketPriority)}
            >
              {TICKET_PRIORITIES.map((priority) => (
                <option key={priority} value={priority}>
                  {t(ticketPriorityLabelKey(priority))}
                </option>
              ))}
            </select>
          ) : (
            <span className="chat-meta-pill">{t(ticketPriorityLabelKey(ticket.priority))}</span>
          )}

          <span className="chat-meta-pill">{ticket.lang.toUpperCase()}</span>
        </div>

        {ticket.escalation && (
          <p className="chat-meta-note">
            {t('dashboard.support.escalationReason', { reason: ticket.escalation.reason })}
            {' · '}
            {dayjs(ticket.escalation.at).format('DD MMM HH:mm')}
          </p>
        )}

        {/* An agent-only chat never runs the model; "0 in / 0 out" is noise. */}
        {prompt + completion > 0 && (
          <p className="chat-meta-note">
            {t('dashboard.support.tokenUsage', { prompt, completion })}
          </p>
        )}
      </div>

      {actions && <div className="chat-header-actions">{actions}</div>}
    </header>
  );
}

export default TicketDetailHeader;
